import { Tabs } from "@arco-design/web-react";
import { useState } from "react";

import { useCurrentAuthUser } from "../../auth";
import { canUsePermission } from "../../../lib/auth";
import { useSysDeptUserRuntime } from "../runtime/useSysDeptUserRuntime";
import { SysDeptTreePanel } from "./SysDeptTreePanel";
import { SysExistingStaffSelectModal } from "./SysExistingStaffSelectModal";
import { SysStaffFilterPanel } from "./SysStaffFilterPanel";
import { SysStaffPasswordResetModal } from "./SysStaffPasswordResetModal";
import { SysStaffResultTable } from "./SysStaffResultTable";
import { SysDeptMutationModal } from "./SysDeptMutationModal";
import { SysStaffMutationModal } from "./SysStaffMutationModal";
import { SysStaffSupervisorsModal } from "./SysStaffSupervisorsModal";
import type { SysDeptTreeNode, SysStaffItem } from "../types";
import "./SysDeptUserPage.css";

type DeptModalState = { type: "create"; parentId?: number } | { type: "edit"; dept: SysDeptTreeNode };
type StaffModalState = { type: "create"; selectedDeptId?: number; forceStaff?: boolean } | { type: "edit"; staff: SysStaffItem };
type ExistingStaffModalState = { deptId: number; deptName: string };

/** 组织和用户页面主体。 */
export function SysDeptUserFeaturePage() {
  const runtime = useSysDeptUserRuntime();
  const currentUser = useCurrentAuthUser().data;
  const canManage = canUsePermission(currentUser, "system.dept_user:manage");
  const canGrantSuperuser = canUsePermission(
    currentUser,
    "system.dept_user:grant_superuser",
  );
  const [activeTab, setActiveTab] = useState("depts");
  const [deptModalState, setDeptModalState] = useState<DeptModalState | null>(null);
  const [staffModalState, setStaffModalState] = useState<StaffModalState | null>(null);
  const [existingStaffModalState, setExistingStaffModalState] = useState<ExistingStaffModalState | null>(null);
  const [supervisorModalState, setSupervisorModalState] = useState<{ staff: SysStaffItem } | null>(null);
  const [passwordResetModalState, setPasswordResetModalState] = useState<{ staff: SysStaffItem } | null>(null);

  const selectedDept = findDeptById(runtime.deptTree, runtime.selectedDeptId);

  return (
    <main className="sys-dept-user-page">
      <Tabs
        activeTab={activeTab}
        className="sys-dept-user-tabs"
        destroyOnHide
        type="rounded"
        onChange={(nextTab) => {
          setActiveTab(nextTab);
          if (nextTab === "staff") {
            runtime.handleShowAllStaff();
          }
        }}
      >
        <Tabs.TabPane key="depts" title="组织部门">
          <section className="sys-dept-user-column sys-dept-user-dept-panel" data-testid="sys-dept-user-dept-panel">
            <SysDeptTreePanel
              canManage={canManage}
              items={runtime.deptTree}
              selectedDeptId={runtime.selectedDeptId}
              includeDisabled={runtime.includeDisabledDepts}
              onIncludeDisabledChange={runtime.setIncludeDisabledDepts}
              onSelectDept={runtime.setSelectedDeptId}
              onCreateDept={(parentId) => setDeptModalState({ type: "create", parentId })}
              onEditDept={(dept) => setDeptModalState({ type: "edit", dept })}
              onEditStaff={(userId) => {
                const staff = findStaffById(runtime.candidateStaffItems, userId);
                if (staff) {
                  setStaffModalState({ type: "edit", staff });
                }
              }}
              onEditStaffSupervisors={(userId) => {
                const staff = findStaffById(runtime.candidateStaffItems, userId);
                if (staff) {
                  setSupervisorModalState({ staff });
                }
              }}
              onCreateStaff={(deptId) => setStaffModalState({ type: "create", selectedDeptId: deptId })}
              onAddExistingStaff={(deptId) => {
                const targetDept = findDeptById(runtime.deptTree, deptId);
                runtime.setSelectedDeptId(deptId);
                setExistingStaffModalState({
                  deptId,
                  deptName: targetDept?.deptName ?? "当前部门",
                });
              }}
              onRemoveStaffFromDept={(userId, deptId) => {
                const staff = findStaffById(runtime.candidateStaffItems, userId);
                if (staff) {
                  void runtime.handleRemoveStaffFromDept(staff, deptId);
                }
              }}
            />
          </section>
        </Tabs.TabPane>
        <Tabs.TabPane key="staff" title="用户">
          <section className="sys-dept-user-column">
            <SysStaffFilterPanel
              filters={runtime.draftFilters}
              errorMessage={runtime.errorMessage}
              onChange={runtime.setDraftFilters}
              onReset={runtime.handleReset}
              onSearch={runtime.handleSearch}
            />
            <SysStaffResultTable
              canManage={canManage}
              items={runtime.items}
              loading={runtime.loading}
              page={runtime.page}
              pageSize={runtime.pageSize}
              total={runtime.total}
              selectedDeptId={runtime.selectedDeptId}
              selectedDeptName={selectedDept?.deptName ?? ""}
              onCreateStaff={() => setStaffModalState({ type: "create", selectedDeptId: runtime.selectedDeptId })}
              onEditStaff={(staff) => setStaffModalState({ type: "edit", staff })}
              onResetPassword={(staff) => setPasswordResetModalState({ staff })}
              onToggleStaffStatus={runtime.handleToggleStaffStatus}
              onMoveStaffToSelectedDept={runtime.handleMoveStaffToSelectedDept}
              onEditSupervisors={(staff) => setSupervisorModalState({ staff })}
              onPageChange={runtime.handlePageChange}
              onPageSizeChange={runtime.handlePageSizeChange}
            />
          </section>
        </Tabs.TabPane>
      </Tabs>
      <SysDeptMutationModal
        state={deptModalState}
        deptOptions={runtime.deptOptions}
        staffOptions={runtime.staffOptions}
        errorMessage={runtime.mutationErrorMessage}
        onClose={() => setDeptModalState(null)}
        onClearError={runtime.clearMutationError}
        onCreateDept={runtime.handleCreateDept}
        onUpdateDept={runtime.handleUpdateDept}
      />
      <SysExistingStaffSelectModal
        state={existingStaffModalState}
        items={runtime.candidateStaffItems}
        errorMessage={runtime.mutationErrorMessage}
        onClose={() => setExistingStaffModalState(null)}
        onClearError={runtime.clearMutationError}
        onAddStaffToDept={runtime.handleAddStaffToDept}
      />
      <SysStaffMutationModal
        canGrantSuperuser={canGrantSuperuser}
        state={staffModalState}
        deptOptions={runtime.deptOptions}
        errorMessage={runtime.mutationErrorMessage}
        onClose={() => setStaffModalState(null)}
        onClearError={runtime.clearMutationError}
        onCreateStaff={runtime.handleCreateStaff}
        onUpdateStaff={runtime.handleUpdateStaff}
      />
      <SysStaffSupervisorsModal
        state={supervisorModalState}
        availableStaffOptions={runtime.candidateStaffItems}
        errorMessage={runtime.mutationErrorMessage}
        onClose={() => setSupervisorModalState(null)}
        onClearError={runtime.clearMutationError}
        onLoadSupervisors={runtime.handleLoadSupervisors}
        onSaveSupervisors={runtime.handleSaveSupervisors}
      />
      <SysStaffPasswordResetModal
        state={passwordResetModalState}
        errorMessage={runtime.mutationErrorMessage}
        onClose={() => setPasswordResetModalState(null)}
        onClearError={runtime.clearMutationError}
        onResetPassword={runtime.handleResetStaffPassword}
      />
    </main>
  );
}

/** 在用户候选列表中查找用户。 */
function findStaffById(items: SysStaffItem[], userId: number): SysStaffItem | undefined {
  return items.find((item) => item.id === userId);
}

/** 在部门树中查找当前选中部门。 */
function findDeptById(items: SysDeptTreeNode[], deptId?: number): SysDeptTreeNode | undefined {
  if (!deptId) {
    return undefined;
  }
  for (const item of items) {
    if (item.id === deptId) {
      return item;
    }
    const child = findDeptById(item.children, deptId);
    if (child) {
      return child;
    }
  }
  return undefined;
}
